import { useWatch } from 'react-hook-form';
import type { NameValue } from 'v5/core/entity-values/values.types';
import {
  FormControl,
  FormField,
  FormItem,
  FormMessage
} from 'v5/design-system/Form';
import { Input } from 'v5/design-system/Input';
import { useFieldContext } from '../FieldContext';
import { fieldAnchor } from '../unitPath';
import { EmptyField, partPlaceholder } from './EmptyField';
import { FieldControlBase } from './FieldControlBase';
import type { FieldComponentProps } from './fieldComponent.types';

/**
 * `{ first_name, last_name }` (v5 `createNameControl`): one "full name" input
 * bound to `first_name` for `layout: 'single'`, or two inputs side by side.
 */
export function NameField({
  spec,
  path,
  hideLabel
}: FieldComponentProps<'name'>) {
  const ctx = useFieldContext();
  const readOnly = ctx.mode === 'display';
  const name: NameValue | undefined = useWatch({ name: path });
  const empty =
    (name?.first_name ?? '') === '' &&
    (spec.layout === 'single' || (name?.last_name ?? '') === '');

  if (readOnly && empty) {
    return <EmptyField spec={spec} path={path} hideLabel={hideLabel} />;
  }

  if (spec.layout === 'single') {
    return (
      <FormField
        name={`${path}.first_name`}
        render={({ field }) => (
          <FieldControlBase spec={spec} hideLabel={hideLabel}>
            <Input
              readOnly={readOnly}
              variant={ctx.variant}
              size={ctx.size}
              placeholder="Enter full name"
              {...field}
              value={field.value ?? ''}
            />
          </FieldControlBase>
        )}
      />
    );
  }

  return (
    <FieldControlBase spec={spec} hideLabel={hideLabel} path={path}>
      <div className="flex flex-row gap-2">
        <FormField
          name={`${path}.first_name`}
          render={({ field }) => (
            <FormItem className="flex-1" {...fieldAnchor(field.name)}>
              <FormControl>
                <Input
                  readOnly={readOnly}
                  variant={ctx.variant}
                  size={ctx.size}
                  placeholder={partPlaceholder(
                    ctx.mode,
                    'Enter first name',
                    (field.value ?? '') === ''
                  )}
                  {...field}
                  value={field.value ?? ''}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          name={`${path}.last_name`}
          render={({ field }) => (
            <FormItem className="flex-1" {...fieldAnchor(field.name)}>
              <FormControl>
                <Input
                  readOnly={readOnly}
                  variant={ctx.variant}
                  size={ctx.size}
                  placeholder={partPlaceholder(
                    ctx.mode,
                    'Enter last name',
                    (field.value ?? '') === ''
                  )}
                  {...field}
                  value={field.value ?? ''}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
      </div>
    </FieldControlBase>
  );
}
